/**
 * Dashboard — the landing screen after sign-in (FR56).
 *
 * It leads with the only two numbers that say whether the product is working:
 * **AI acceptance rate**, which must climb, and **how often the catalog still
 * has to ask**, which must fall. Everything else on the screen is context.
 *
 * Charts are inline SVG, single-series, in one hue — so there is no categorical
 * palette to get wrong, and the value is direct-labelled rather than relying on
 * colour to carry meaning.
 */

import { useSession } from '../app/SessionProvider.js';
import { STATUS_LABEL } from '../fixtures/shop.js';
import type { DashboardMetrics } from '../data/repo.js';

function Spark({ values, w = 116, h = 26 }: { values: number[]; w?: number; h?: number }) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => [
    (i / (values.length - 1)) * w,
    h - ((v - min) / range) * (h - 4) - 2,
  ] as const);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1]!;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="spark" aria-hidden="true">
      <path d={d} />
      <circle cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r="2.6" />
    </svg>
  );
}

function LineChart({ labels, values, unit }: { labels: string[]; values: number[]; unit: string }) {
  const W = 420, H = 150, L = 30, R = 8, T = 10, B = 20;
  const iw = W - L - R, ih = H - T - B;
  const lo = Math.floor(Math.min(...values) / 10) * 10;
  const hi = Math.ceil(Math.max(...values) / 10) * 10;
  const X = (i: number) => L + (i / (labels.length - 1)) * iw;
  const Y = (v: number) => T + ih - ((v - lo) / (hi - lo || 1)) * ih;
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img"
         aria-label={`${values[0]}${unit} rising to ${values[values.length - 1]}${unit}`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const v = lo + ((hi - lo) * i) / 4;
        return (
          <g key={i}>
            <line className="grid" x1={L} y1={Y(v)} x2={W - R} y2={Y(v)} />
            <text className="axis" x={L - 6} y={Y(v) + 3} textAnchor="end">{Math.round(v)}</text>
          </g>
        );
      })}
      <path className="series" d={d} />
      {values.map((v, i) => <circle key={i} className="dot" cx={X(i)} cy={Y(v)} r="3" />)}
      {labels.map((l, i) => (i % 3 === 0 || i === labels.length - 1) && (
        <text key={l} className="axis" x={X(i)} y={H - 6} textAnchor="middle">{l}</text>
      ))}
    </svg>
  );
}

function Bars({ labels, values }: { labels: string[]; values: number[] }) {
  const W = 420, H = 140, L = 26, T = 8, B = 22;
  const iw = W - L - 6, ih = H - T - B;
  const max = Math.max(...values) || 1;
  const bw = iw / labels.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img"
         aria-label={`${values[0]} prompts a week falling to ${values[values.length - 1]}`}>
      {[0, 1, 2].map((i) => {
        const y = T + ih - (ih * i) / 2;
        return (
          <g key={i}>
            <line className="grid" x1={L} y1={y} x2={W - 6} y2={y} />
            <text className="axis" x={L - 6} y={y + 3} textAnchor="end">
              {Math.round((max * i) / 2)}
            </text>
          </g>
        );
      })}
      {values.map((v, i) => {
        const h = Math.max(2, (v / max) * ih);
        return (
          <rect key={i} className="bar" rx="3"
                x={L + i * bw + bw * 0.18} y={T + ih - h}
                width={bw * 0.64} height={h} />
        );
      })}
      {labels.map((l, i) => (
        <text key={l} className="axis" x={L + i * bw + bw / 2} y={H - 7} textAnchor="middle">{l}</text>
      ))}
    </svg>
  );
}

export function Dashboard({ metrics }: { metrics: DashboardMetrics }) {
  const { t, money, session } = useSession();
  const { openJobs, awaitingApproval, invoicedThisMonth, weeks, catalogPrompts } = metrics;

  // A week where nothing was decided has no rate — not a zero. Charting a zero
  // there would draw a collapse that never happened.
  const acceptance = metrics.acceptance.filter((v): v is number => v !== null);
  const latestAcceptance = acceptance[acceptance.length - 1] ?? null;
  const priorAcceptance = acceptance[acceptance.length - 2] ?? null;
  const acceptanceDelta = latestAcceptance !== null && priorAcceptance !== null
    ? latestAcceptance - priorAcceptance : null;
  const promptsNow = catalogPrompts[catalogPrompts.length - 1] ?? 0;
  const promptsPrior = catalogPrompts[catalogPrompts.length - 2] ?? promptsNow;
  const promptsFirst = catalogPrompts[0] ?? 0;

  // Direction has to drive the colour. Both captions used to be hardcoded
  // `cap up` — green, the colour of good news — so a FALLING acceptance rate
  // and a RISING prompt count were both congratulated. These are the two
  // numbers the product is judged on; they must not flatter themselves.
  const trend = (delta: number | null, goodWhenRising: boolean) => {
    if (delta === null || delta === 0) return 'cap';
    return `cap ${(delta > 0) === goodWhenRising ? 'up' : 'down'}`;
  };

  return (
    <section className="screen">
      <h2>{t('Dashboard')}</h2>
      <p className="muted lede">
        {session?.shop.name} · last twelve weeks. The two numbers that decide whether
        this is working are <b>acceptance rate</b> and <b>how often the catalog still
        has to ask</b>.
      </p>

      <div className="tiles">
        <div className="tile">
          <div className="lbl">Open jobs</div>
          <div className="val">{openJobs}</div>
          <div className="cap">{awaitingApproval} awaiting approval</div>
        </div>
        <div className="tile">
          <div className="lbl">AI acceptance rate</div>
          <div className="val">
            {latestAcceptance === null ? '—' : latestAcceptance}<small>%</small>
          </div>
          <div className={trend(acceptanceDelta, true)}>
            {latestAcceptance === null ? 'no decisions yet'
              : acceptanceDelta === null ? 'first week of decisions — nothing to compare yet'
              : `${acceptanceDelta >= 0 ? '▲' : '▼'} ${Math.abs(acceptanceDelta)} pts on last week`}
          </div>
          {acceptance.length > 1 && <Spark values={acceptance} />}
        </div>
        <div className="tile">
          <div className="lbl">Catalog asked this week</div>
          <div className="val">{promptsNow}</div>
          <div className={trend(promptsNow - promptsPrior, false)}>
            {promptsNow <= promptsPrior ? '▼' : '▲'} {Math.abs(promptsNow - promptsPrior)}
            {' · falling means it is working'}
          </div>
          {catalogPrompts.length > 1 && <Spark values={catalogPrompts.slice(-7)} />}
        </div>
        <div className="tile">
          <div className="lbl">Invoiced</div>
          <div className="val small-val">{money(invoicedThisMonth)}</div>
          <div className="cap">this month</div>
        </div>
      </div>

      <div className="cards">
        <div className="card">
          <h3>AI acceptance rate</h3>
          <p className="muted small">
            Share of suggestions kept, by week. Below 50% the capture loop costs more
            than it saves.
          </p>
          <LineChart labels={weeks.slice(-acceptance.length)} values={acceptance} unit="%" />
        </div>

        <div className="card">
          <h3>How often the catalog still has to ask</h3>
          <p className="muted small">
            Prompts per week. Falling is the whole design working — {promptsFirst}{' '}
            {promptsNow <= promptsFirst ? 'down to' : 'up to'} {promptsNow}.
          </p>
          <Bars labels={weeks} values={catalogPrompts} />
        </div>
      </div>

      <div className="card">
        <h3>Jobs by status</h3>
        <div className="statusrow">
          {metrics.byStatus.map(({ status, count }) => (
            <div key={status} className={`statpill ${status}`}>
              <b>{count}</b><span>{STATUS_LABEL[status]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* "Open" above hides whether a job is one photograph short or one
          saved price from going out, and says nothing about the ones already
          sent for approval — those are still yours to track, just waiting on
          someone else. One bar, every job that isn't closed. */}
      <div className="card">
        <h3>Where your jobs are</h3>
        {metrics.byStage.capture + metrics.byStage.review + metrics.byStage.price
          + metrics.awaitingApproval === 0 ? (
          <p className="muted small">Nothing open and nothing waiting on a customer right now.</p>
        ) : (
          <>
            <div className="stagebar">
              {metrics.byStage.capture > 0 && (
                <div className="stagebar-seg capture" style={{ flex: metrics.byStage.capture }}>
                  <b>{metrics.byStage.capture}</b><span>Capture</span>
                </div>
              )}
              {metrics.byStage.review > 0 && (
                <div className="stagebar-seg review" style={{ flex: metrics.byStage.review }}>
                  <b>{metrics.byStage.review}</b><span>Review</span>
                </div>
              )}
              {metrics.byStage.price > 0 && (
                <div className="stagebar-seg price" style={{ flex: metrics.byStage.price }}>
                  <b>{metrics.byStage.price}</b><span>Price</span>
                </div>
              )}
              {metrics.awaitingApproval > 0 && (
                <div className="stagebar-seg waiting" style={{ flex: metrics.awaitingApproval }}>
                  <b>{metrics.awaitingApproval}</b><span>Customer</span>
                </div>
              )}
            </div>
            <div className="stagebar-legend">
              <span><i className="capture" />Capture — {metrics.byStage.capture}</span>
              <span><i className="review" />Review — {metrics.byStage.review}</span>
              <span><i className="price" />Price — {metrics.byStage.price}</span>
              <span><i className="waiting" />Awaiting customer — {metrics.awaitingApproval}</span>
            </div>
          </>
        )}
        {metrics.needsYou.length > 0 && (
          <div className="attention">
            <h4>{metrics.needsYou.length} job{metrics.needsYou.length === 1 ? '' : 's'}{' '}
              waiting on you, not the customer</h4>
            <ul>
              {metrics.needsYou.map((n) => (
                <li key={n.reference}>
                  <span className="ref">{n.reference}</span> {n.customer} — {n.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
